//2019-01-04

//this file may only contain code that is applicable for all pages/the entire site

import request from 'request-promise';
import { EventDebouncerAnimationFrame } from 'event-debouncer';
import getTailwindDevice from 'get-tailwind-device';
import setSplideCustomButtons from 'set-splide-custom-buttons';
import cookie from 'cookie';
import slideToggle from 'slide-toggle';
import extractJson from 'extract-json-payload';

window.jQueryCallbacks = window.jQueryCallbacks || [];

// Track input modality so :focus-visible outline only applies on keyboard nav.
// Client: outline should appear only on Tab, not on mouse click into text inputs.
(function()
{
	var root = document.documentElement;
	document.addEventListener('keydown', function(e)
	{
		if (e.key === 'Tab') root.classList.add('keyboard-nav');
	}, true);
	document.addEventListener('mousedown', function()
	{
		root.classList.remove('keyboard-nav');
	}, true);
	document.addEventListener('touchstart', function()
	{
		root.classList.remove('keyboard-nav');
	}, true);
})();

const createNodeFromHTML = (htmlString) => {
	const frag = document.createRange().createContextualFragment(htmlString);

	return frag;
};

var isDev = !!(typeof (window.location.host) === 'string' && window.location.host.endsWith('.local'));
var internalPath = document.querySelector('body').getAttribute('data-internal-path');
var isPhysicalStore = !!(window.location.pathname.startsWith('/physical-store/'));

// Splide default settings.
// Assigned as this module runs, not on DOMContentLoaded: the carousels below mount from fetch callbacks that
// can resolve before DOMContentLoaded fires, and those mounts used to get Splide's own defaults instead of
// ours. Splide loads as a deferred classic script in the head (see System/Med24.php), so the global is always
// here by now. 2026-10-19 MED-80
Splide.defaults =
{
	type: 'loop',
	drag: 'free',
	omitEnd: true,
	arrows: false,
	snap: true,
	mediaQuery: 'min',
	perPage: 2,
	pagination: false,
	breakpoints: {
		0: { perPage: 2, gap: '0.75rem' },
		640: { perPage: 2, gap: '2rem' },
		768:  { perPage: 2 },
		1024:  { perPage: 4 },
		1280: { perPage: 4 },
		1536: { perPage: 5 },
	}
};

window.scrollToElement = function(targetElement, offset) {
	if (typeof jQuery !== 'undefined' && targetElement instanceof jQuery) targetElement = targetElement.get(0);

	offset = offset || 10;

	window.scroll(
		{
			top: targetElement.offsetTop - offset,
			behavior: 'smooth'
		});
};

//get cookies (necessary for pages 100% cached by varnish)
request.postURL('/getCookies' + window.location.search, { fullPath: window.location.href });

//attach proper classes to blockedit iframes (youtube videos)
const blockEditContentIframes = document.querySelectorAll('div.blockEditContent iframe');
blockEditContentIframes.forEach(iframe => {
	iframe.classList.add('embed-responsive-item');

	const wrapper = document.createElement('div');
	wrapper.classList.add('embed-responsive', 'embed-responsive-16by9');

	iframe.parentNode.insertBefore(wrapper, iframe);

	wrapper.appendChild(iframe);
});

//mobile/desktop specific code
var mobileOnlyInit = async () => {
	if (isPhysicalStore) return;
	if (isDev) console.log('Mobile-Only Init: started');

	var [initMobileMenuModule, SlideMenuModule] = await Promise.all([import('./modules/mobile-menu.js'), import('./modules/slide-menu.js')]);

	const initMobileMenu = initMobileMenuModule.default;
	const SlideMenu = SlideMenuModule.default;

	initMobileMenu(isDev);

	const slideMenu = new SlideMenu(document.querySelector('section.links .footerMenu'));

	slideMenu.init();

	if (isDev) console.log('Mobile-Only Init: completed');
};

var desktopOnlyInit = function() {
	if (isDev) console.log('Desktop-Only Init: started');

	//...

	if (isDev) console.log('Desktop-Only Init: completed');
};

var mobileLoaded = false;
var desktopLoaded = false;
window.addEventListener('bm:bsDeviceFamilyChange', event => {
	const bsDeviceInfo = event.detail;

	if (!mobileLoaded && bsDeviceInfo.current === 'mobile') {
		mobileLoaded = true;

		mobileOnlyInit();
	}

	if (!desktopLoaded && bsDeviceInfo.current === 'desktop') {
		desktopLoaded = true;

		desktopOnlyInit();
	};
});

window.addEventListener('med24loaded', event => {
	//update cart quantity
	med24.updateCartItemCount();

	const { searchParams } = new URL(window.servedURL || window.location.href);	// MED-66: the address bar no longer has discountCode
	if (searchParams.has('discountCode')) {
		med24.addDiscountCodeFromURL(searchParams.get('discountCode'))
	}
});

//add "active-indicator" to main menu
const currentMenuContent = document.querySelector('.mainMenu li[data-content-id="' + window.currentMenuContentID + '"]');
if (currentMenuContent) currentMenuContent.classList.add('active');

//add click handle for cms popup links
var addShowContentBlockEventListeners = function(container) {
	container.querySelectorAll('[data-content-block-id]').forEach(contentBlock => {
		contentBlock.addEventListener('click', async () => {
			const showContentBlock = (await import('./modules/show-content-block.js')).default;

			showContentBlock(contentBlock.dataset.contentBlockId);
		});
	});
};

addShowContentBlockEventListeners(document);

//load blockeditcontent product grids
document.querySelectorAll('.blockEditContent div.productGrid').forEach((container) => {
	var data = {};
	data.context = container.getAttribute('data-filter-context');
	data.items = container.getAttribute('data-product-count')*1;
	data.sortBy = container.getAttribute('data-sort-by');
	data.onlyBuyable = container.getAttribute('data-only-buyable');
	data.selectedProductIDs = JSON.parse(container.getAttribute('data-selected-productIDs') || '[]');
	data.page = 1;
	data.skipGridBanners = 'true'
	data.alternateTemplate = 'splideProductThumb.tpl';
	
	request.postURL('/esi/product-filter', data).then(response => {

		const responseDom = createNodeFromHTML(response);
		extractJson(responseDom, 'payload'); //just to check if it's valid json and delete script tag
		container.append(responseDom);
		
		// if productGrid is carousel.
		if (container.classList.contains('splide__list'))
		{
			// Splide reads `.splide__track` off the root it is handed, so the root has to be the real `.splide`
			// element: a wrong one throws inside the library instead of here. Counting two levels up assumed
			// exactly `.splide > .splide__track > .splide__list`, which the block-edit markup does not always
			// produce, so ask the DOM for the root and skip the mount when there is none. 2026-10-19 MED-80
			const splideElement = container.closest('.splide');

			if (!splideElement) return;

			const breakpointViewportItems = JSON.parse(container.getAttribute('data-breakpoint-viewport-items') || '{}');

			// to find preDefined splide settings search for 'Splide.defaults' in med24-global.js
			const splideInstance = new Splide( splideElement, {
				padding: { left: '2px', right: '2rem'},
				breakpoints: 
				{
					0: { perPage: breakpointViewportItems['xs'] || 2 },
					640: { perPage: breakpointViewportItems['sm'] || 2, padding: { left: '2px', right: '4rem'} },
					768: { perPage: breakpointViewportItems['md'] || 3 },
					1024: { perPage: breakpointViewportItems['lg'] || 4 },
					1280: { perPage: breakpointViewportItems['xl'] || 4 },
					1536: { perPage: breakpointViewportItems['2xl'] || 4 }
				},	
			} ).mount();

			setSplideCustomButtons(splideElement, splideInstance);
		}
	});
});

document.querySelectorAll('.blockEditContent div.imageCarousel').forEach((splideElement) =>
{
	const breakpointViewportItems = JSON.parse(splideElement.getAttribute('data-breakpoint-viewport-items') || '{}');
	const autoSlideInterval = parseInt(splideElement.getAttribute('data-auto-slide-interval')) * 1000 || 0; //seconds to ms

	document.addEventListener( 'DOMContentLoaded', function() 
	{  
		// to find preDefined splide settings search for 'Splide.defaults' in med24-global.js
		const splideInstance = new Splide( splideElement, {
			type: 'loop',
			drag: true,
			perPage: 1,
			perMove: 1,
			autoplay: autoSlideInterval > 0,
			pauseOnHover: true,
			pauseOnFocus: false,
			interval: autoSlideInterval,
			breakpoints: 
			{
				0: { perPage: breakpointViewportItems['xs'] || 2 },
				640: { perPage: breakpointViewportItems['sm'] || 2 },
				768: { perPage: breakpointViewportItems['md'] || 3 },
				1024: { perPage: breakpointViewportItems['lg'] || 4 },
				1280: { perPage: breakpointViewportItems['xl'] || 4 },
				1536: { perPage: breakpointViewportItems['2xl'] || 4 }
			},	
		} ).mount();

		splideInstance.on('move', () =>
		{
			let currentIndex = splideInstance.index;

			const indicators = splideElement.querySelectorAll('.slideIndicators .slideIndicator');
			if (!indicators) return;
			
			for (let i = 0; i < indicators.length; i++) {
				const indicator = indicators[i];
				if (!indicator) continue;

				// Toggle the loading class only on the active one
				indicator.classList.toggle('tw-animate-loading', i === currentIndex);

				// Add 'tw-w-100' to all previous indicators (excluding first only if needed)
				if (i < currentIndex && autoSlideInterval !== 0) indicator.classList.add('tw-w-full');
				else indicator.classList.remove('tw-w-full');
			}
		});
	});
});

//init FAQ elements in blockedit
document.querySelectorAll('.blockEditContent [data-faq-style="collapse"] .question').forEach((container) => {
	container.addEventListener('click', (event) => {
		var box = container.parentNode;

		if (box.classList.contains('open')) {
			slideToggle(box.querySelector('.answer'), 500, 'up').then(function() {
				box.classList.remove('open');
			});
		}
		else {
			slideToggle(box.querySelector('.answer'), 500, 'down').then(function() {
				box.classList.add('open');
			});
		}
	});
});

//init show more/less.button on CMSs
document.querySelectorAll('.blockEditContent.showLessMobile').forEach((container) => {
	var htmlNode = document.createElement('span');

	htmlNode.classList.add('showMore', 'hidden-sm-up', 'icon', 'icon-chevron-down');

	container.parentNode.insertBefore(htmlNode, container.nextSibling);

	var element = container;

	var overflown = element.scrollHeight > element.clientHeight;

	if (!overflown) {
		container.classList.add('contentShown');
	}

	container.addEventListener('click', (event) => {
		if (!container.classList.contains('contentShown')) event.preventDefault();;

		container.classList.add('contentShown');
	});

	htmlNode.addEventListener('click', (event) => {
		htmlNode.previousSibling.classList.add('contentShown');
	});
});

//load async blocks
var asyncBlockPromises = [];

document.querySelectorAll('[data-async-block]').forEach(container =>
{
	var name = container.getAttribute('data-async-block');
	var mode = container.getAttribute('data-async-block-mode') || 'set';
	var params = container.getAttribute('data-async-block-params') || '';
	var requestMethod = container.getAttribute('data-async-block-method') || 'post';

	var blockParams = {};
	if (params.length > 0) {
		params.split('|').forEach(kvPair => {
			var kvSeparatorIndex = kvPair.indexOf(':');

			if (kvSeparatorIndex === -1) return;

			var key = kvPair.substr(0, kvSeparatorIndex);
			var value = kvPair.substr(kvSeparatorIndex + 1);

			blockParams[key] = value;
		});
	}
	
	var method = (requestMethod === 'post'?'postURL':'getURL');

	const endpoint = '/asyncBlock/'+name;
	const promiseKey = method+'_'+endpoint+'_'+JSON.stringify(blockParams);
	
	if(!asyncBlockPromises[promiseKey]) asyncBlockPromises[promiseKey] = request[method](endpoint, blockParams);
	
	asyncBlockPromises[promiseKey].then(response =>
	{
		if(response.status < 0)
		{
			console.log('Failed to load asyncBlock ('+name+'): '+response.message);
			return;
		}

		const htmlBody = createNodeFromHTML(response.html);

		if (mode === 'append') {
			container.appendChild(htmlBody);
		}
		else if (mode === 'set') {
			container.innerHTML = '';
			container.appendChild(htmlBody);
		}
		else if (mode === 'prepend') {
			container.prepend(htmlBody);
		}

		container.dispatchEvent(new CustomEvent('async-block-loaded', { detail: { name: name } }));

		addShowContentBlockEventListeners(container);
	});
});

//fire device events
let lastTwDevice = null;
var resizeDebouncer = new EventDebouncerAnimationFrame(() =>
{
	
	let currentTwDevice = getTailwindDevice();
	if(currentTwDevice !== lastTwDevice)
	{
		window.dispatchEvent(new CustomEvent('bm:twDeviceChange', {detail: {current:currentTwDevice,previous:lastTwDevice}}));
		lastTwDevice = currentTwDevice;
	}
});

window.addEventListener('resize', resizeDebouncer.event);

const twDeviceFamilyMap =
{
	'xxs':'mobile', // Mobile Phone
	'xs':'mobile', // Mobile phone
	'sm':'mobile', // Normal tablet
	'md':'mobile', // Big tablet
	'lg':'desktop', // Desktop
	'xl':'desktop', // Desktop
	'2xl':'desktop', // Desktop
};

let lastTwDeviceFamily = null;

window.addEventListener('bm:twDeviceChange', (event) =>
{
	const twDeviceInfo = event.detail;

	window.currentTwDevice = twDeviceInfo.current;
	
	const currentTwDeviceFamily = twDeviceFamilyMap[twDeviceInfo.current];
	
	if(currentTwDeviceFamily !== lastTwDeviceFamily)
	{
		//console.log('event: bm:bsDeviceFamilyChange');
		window.dispatchEvent(new CustomEvent('bm:bsDeviceFamilyChange', {detail:{current:currentTwDeviceFamily,previous:lastTwDeviceFamily}}));
		
		lastTwDeviceFamily = currentTwDeviceFamily;
	}
});

resizeDebouncer.event();

//country popup
if (cookie.getCookie('CountryPopupShown') !== 'true') {
	cookie.setCookie('CountryPopupShown', 'true');

	request.postURL('/getCountryPopup.json', {}).then(async (response) => {
		if (response.status !== 0) return;

		const alerts = (await import('./modules/alerts-promise.js')).default;
		const t = (await import('./modules/translate.js')).default;

		const buttons =
			[
				{
					title: t('Cancel'),
					name: 'cancel',
					style: 'neutral',
					action: 'resolve'
				},
				{
					title: t('Go to %s', response.storeName),
					name: 'go',
					style: 'positive',
					action: 'resolve'
				}
			];

		const result = await alerts.createDialog(response.content, 'html', t('Want to try %s?', response.storeName), buttons, false, overlay => {
			overlay.buttons.go.focus();
		});

		if (result.name === 'go') window.location = response.storeURL;
	});
}

//automatically register promoViews and promoClicks on images in cms areas
var blockEditPromoViews = [];

document.querySelectorAll('.blockEditContent img').forEach(img => 
{
	if (!(typeof (img.getAttribute('alt')) !== 'undefined')) return;

	const alt = img.getAttribute('alt').trim();
	let mainElement = img;
	let href = null;

	if (alt.length === 0) return;

	if (img.parentNode.classList.contains('imageLink')) {
		mainElement = img.parentNode;
		href = mainElement.href;
	}

	// Tagging for Matomo Content Tracking
	img.setAttribute('data-track-content', '');
	img.setAttribute('data-content-name', alt);
	img.setAttribute('data-content-piece', img.src);
	img.setAttribute('data-content-target', href);

	var promoFieldObject =
	{
		'id': mainElement.getAttribute('data-block-uuid'),
		'name': alt,
	};

	blockEditPromoViews.push(promoFieldObject);

	mainElement.addEventListener('click', event => {
		var eventObject =
		{
			'event': 'promotionClick',
			'ecommerce':
			{
				'promoClick':
				{
					'promotions':
						[
							promoFieldObject
						]
				}
			}
		};

		if (typeof (href) === 'string') {
			if (typeof(window.google_tag_manager) === 'undefined') return;

			event.preventDefault();

			eventObject.eventCallback = () => window.open(href, mainElement.getAttribute('target'));
		}

		window.dataLayer.push(eventObject);

	});
});

if (blockEditPromoViews.length > 0) {
	window.dataLayer.push
		({
			event: 'promoView',
			ecommerce:
			{
				promoView:
				{
					promotions: blockEditPromoViews
				}
			}
		});
}

//sticky header
var header = document.querySelector('header .second');
var stickyHeader = document.querySelector('header .stickyHeader');
var categoryMenu = document.querySelector('.categoryMenu') || null;
var categoryMenuSticky = document.querySelector('.categoryMenuSticky') || null;
var stickyShown = false;

var scrollHysteresis = 100;

if (header) {
	var stickyShownPrev = null

	const scrollDebouncer = new EventDebouncerAnimationFrame(() => {
		stickyShown = !!(window.scrollY < header.offsetTop);
		if(stickyShown !== stickyShownPrev || stickyShownPrev === null)
		{
			if(!stickyShown)
			{
				if(categoryMenuSticky !== null && categoryMenuSticky.childElementCount !== 0) stickyHeader.classList.add('categoryMenuShown'); //we want this to happen first
				
				stickyHeader.classList.add('pinned');

				if (categoryMenuSticky !== null) categoryMenuSticky.scrollLeft = categoryMenu.scrollLeft; //this has to happen after the element is shown otherwise you can not set the scrollLeft
			}
			else {
				if (categoryMenu !== null) categoryMenu.scrollLeft = stickyHeader.querySelector('.categoryMenuSticky').scrollLeft; //can not be set if it's hidden so has to happen first

				stickyHeader.classList.remove('pinned');

				if (categoryMenu !== null) stickyHeader.classList.remove('categoryMenuShown'); //we shrink the menu after hiding it to avoid layout shift
			}
		}

		stickyShownPrev = stickyShown;
	});

	window.addEventListener('scroll', scrollDebouncer.event);
	window.addEventListener('bm:twDeviceChange', scrollDebouncer.event);
	
	scrollDebouncer.event();
}

//document.dispatchEvent(new CustomEvent('med24-global-initialized'));
